/**
 * Cart Drawer functionality
 */

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('CartDrawer');
    this.backdrop = document.getElementById('CartDrawerBackdrop');
    this.cartButtons = document.querySelectorAll('[data-cart-open]');
    this.closeButton = this.drawer?.querySelector('.cart-drawer__close');
    this.form = document.getElementById('CartDrawerForm');
    this.cartUpdateURL = '/cart/update.js';
    this.cartAddURL = '/cart/add.js';
    this.cartChangeURL = '/cart/change.js';
    this.cartURL = '/cart.js';

    this.bindEvents();
    
    // Fetch cart data on page load to ensure correct initial state
    this.fetchCartData();
  }

  bindEvents() {
    // Open cart drawer events
    this.cartButtons?.forEach(button => {
      button.addEventListener('click', this.openDrawer.bind(this));
    });

    // Close cart drawer events
    this.closeButton?.addEventListener('click', this.closeDrawer.bind(this));
    this.backdrop?.addEventListener('click', this.closeDrawer.bind(this));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeDrawer();
    });
    
    // Bind cart item events
    this.bindCartItemEvents();
  }

  openDrawer() {
    this.drawer?.classList.add('is-active');
    this.backdrop?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    
    // Get latest cart data when drawer is opened
    this.fetchCartData();
  }

  closeDrawer() {
    this.drawer?.classList.remove('is-active');
    this.backdrop?.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  async fetchCartData(skipSecondaryRefresh = false) {
    try {
      // First request - this might use cached data
      const response = await fetch(this.cartURL);
      if (!response.ok) throw new Error('Failed to fetch cart data');
      
      const cart = await response.json();
      this.updateCartDrawer(cart);
      
      // For new sessions, make a second request after a delay to get fresh data
      // Only if we're not already in a secondary refresh
      if (!skipSecondaryRefresh) {
        setTimeout(async () => {
          try {
            // Use a cache-busting query param to force a fresh response
            const freshResponse = await fetch(`${this.cartURL}?_=${Date.now()}`);
            if (freshResponse.ok) {
              const freshCart = await freshResponse.json();
              
              // Only update if we have items AND the item count has changed
              // This prevents unnecessary refreshes that cause flashing
              if (freshCart.item_count > 0 && freshCart.item_count !== cart.item_count) {
                console.log('Refreshing cart with fresh data - items changed');
                this.updateCartDrawer(freshCart, true); // Mark as secondary update
              }
            }
          } catch (refreshError) {
            console.error('Error refreshing cart data:', refreshError);
          }
        }, 800);
      }
    } catch (error) {
      console.error('Error fetching cart data:', error);
    }
  }

  updateCartDrawer(cart, isSecondaryUpdate = false) {
    // Update cart count in header
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(el => {
      el.textContent = cart.item_count;
      if (cart.item_count > 0) {
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    });

    // Show/hide empty state based on cart item count
    const emptyMessage = this.drawer?.querySelector('.cart-drawer__empty');
    const cartForm = this.drawer?.querySelector('#CartDrawerForm');
    const cartFooter = this.drawer?.querySelector('.cart-drawer__footer');
    const itemsContainer = this.drawer?.querySelector('.cart-drawer__items');

    if (cart.item_count === 0) {
      // Cart is empty
      emptyMessage?.classList.remove('hidden');
      cartForm?.classList.add('hidden');
      cartFooter?.classList.add('hidden');
    } else {
      // Cart has items
      emptyMessage?.classList.add('hidden');
      cartForm?.classList.remove('hidden'); 
      cartFooter?.classList.remove('hidden');
      
      // Ensure the items container is visible
      if (itemsContainer) {
        itemsContainer.style.display = 'block';
      }
    }

    // Update subtotal
    const subtotalElement = this.drawer?.querySelector('.cart-drawer__subtotal-price');
    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(cart.total_price);
    }

    // Rebuild the cart items HTML - skip animation for secondary updates to prevent flashing
    this.rebuildCartItemsHTML(cart, isSecondaryUpdate);

    // Make sure all buttons are enabled (in case any were left disabled)
    const allControls = this.drawer?.querySelectorAll('button, input');
    allControls?.forEach(control => {
      control.disabled = false;
      
      // If it's an item that was being removed, reset opacity
      const item = control.closest('[data-cart-item]');
      if (item) {
        item.style.opacity = '1';
      }
    });

    // We need to remove old event listeners before adding new ones
    this.unbindCartItemEvents();
    this.bindCartItemEvents();
  }
  
  // Only unbind events related to cart items
  unbindCartItemEvents() {
    const quantityButtons = this.drawer?.querySelectorAll('[data-change-quantity]');
    const removeButtons = this.drawer?.querySelectorAll('[data-remove-item]');
    const quantityInputs = this.drawer?.querySelectorAll('[data-quantity-input]');
    
    // Use cloneNode to remove event listeners
    quantityButtons?.forEach(button => {
      const newButton = button.cloneNode(true);
      if (button.parentNode) {
        button.parentNode.replaceChild(newButton, button);
      }
    });
    
    removeButtons?.forEach(button => {
      const newButton = button.cloneNode(true);
      if (button.parentNode) {
        button.parentNode.replaceChild(newButton, button);
      }
    });
    
    quantityInputs?.forEach(input => {
      const newInput = input.cloneNode(true);
      if (input.parentNode) {
        input.parentNode.replaceChild(newInput, input);
      }
    });
  }
  
  // Only bind events related to cart items
  bindCartItemEvents() {
    // Quantity change events
    const quantityButtons = this.drawer?.querySelectorAll('[data-change-quantity]');
    quantityButtons?.forEach(button => {
      button.addEventListener('click', this.handleQuantityChange.bind(this));
    });

    // Remove item events
    const removeButtons = this.drawer?.querySelectorAll('[data-remove-item]');
    removeButtons?.forEach(button => {
      button.addEventListener('click', this.handleRemoveItem.bind(this));
    });

    // Update cart on quantity input change
    const quantityInputs = this.drawer?.querySelectorAll('[data-quantity-input]');
    quantityInputs?.forEach(input => {
      input.addEventListener('change', this.handleQuantityInputChange.bind(this));
    });
  }

  async handleQuantityChange(event) {
    event.preventDefault();
    
    // Disable the button to prevent multiple clicks
    const button = event.currentTarget;
    if (button.disabled) return;
    button.disabled = true;
    
    try {
      const action = button.dataset.changeQuantity;
      const item = button.closest('[data-cart-item]');
      const itemKey = item?.dataset.cartItemKey;
      const quantityInput = item?.querySelector('[data-quantity-input]');
      
      if (!itemKey || !quantityInput) {
        button.disabled = false;
        return;
      }
      
      // Get current quantity as a number
      const currentQuantity = parseInt(quantityInput.value, 10) || 0;
      
      // Calculate new quantity - explicitly add or subtract 1
      let newQuantity;
      if (action === 'increase') {
        newQuantity = currentQuantity + 1;
      } else if (action === 'decrease') {
        newQuantity = Math.max(0, currentQuantity - 1);
      } else {
        button.disabled = false;
        return;
      }
      
      console.log(`Changing quantity from ${currentQuantity} to ${newQuantity}`);
      
      // Update the input value immediately for visual feedback
      quantityInput.value = newQuantity;
      
      // Disable both buttons during update
      const allButtons = item.querySelectorAll('[data-change-quantity]');
      allButtons.forEach(btn => {
        btn.disabled = true;
      });
      
      // Brief delay to prevent accidental double-clicks
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Send update to cart
      if (newQuantity === 0) {
        // If quantity is 0, remove the item
        await this.updateItemQuantity(itemKey, 0);
      } else {
        // Otherwise update with new quantity
        await this.updateItemQuantity(itemKey, newQuantity);
      }
      
      // Buttons will be re-enabled in updateCartDrawer
    } catch (error) {
      console.error('Error changing quantity:', error);
      // Re-enable the button in case of error
      button.disabled = false;
    }
  }

  async handleQuantityInputChange(event) {
    const input = event.currentTarget;
    
    // Disable input during update
    input.disabled = true;
    
    try {
      const item = input.closest('[data-cart-item]');
      const itemKey = item?.dataset.cartItemKey;
      
      if (!itemKey) {
        input.disabled = false;
        return;
      }
      
      // Get entered quantity as a number, ensure it's at least 0
      const newQuantity = Math.max(0, parseInt(input.value, 10) || 0);
      
      // Update the input value in case it was changed to 0
      input.value = newQuantity;
      
      console.log(`Input changed quantity to ${newQuantity}`);
      
      // Disable quantity buttons during update
      const allButtons = item.querySelectorAll('[data-change-quantity]');
      allButtons.forEach(btn => {
        btn.disabled = true;
      });
      
      // Send update to cart
      await this.updateItemQuantity(itemKey, newQuantity);
      
      // Elements are re-enabled when cart is refreshed
    } catch (error) {
      console.error('Error updating quantity from input:', error);
      // Re-enable the input in case of error
      input.disabled = false;
    }
  }

  async handleRemoveItem(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    if (button.disabled) return;
    button.disabled = true;
    
    try {
      const item = button.closest('[data-cart-item]');
      const itemKey = item?.dataset.cartItemKey;
      
      if (!itemKey) {
        button.disabled = false;
        return;
      }
      
      console.log(`Removing item ${itemKey}`);
      
      // Add visual feedback
      item.style.opacity = '0.5';
      
      // Disable all interactive elements in the item
      const interactiveElements = item.querySelectorAll('button, input');
      interactiveElements.forEach(el => {
        el.disabled = true;
      });
      
      // Remove the item
      await this.updateItemQuantity(itemKey, 0);
      
      // The cart will be refreshed automatically
    } catch (error) {
      console.error('Error removing item:', error);
      // Restore the item on error
      const item = button.closest('[data-cart-item]');
      if (item) {
        item.style.opacity = '1';
        const interactiveElements = item.querySelectorAll('button, input');
        interactiveElements.forEach(el => {
          el.disabled = false;
        });
      }
      button.disabled = false;
    }
  }

  async updateItemQuantity(itemKey, quantity) {
    try {
      console.log(`Sending update to cart: item ${itemKey}, quantity ${quantity}`);
      
      const response = await fetch(this.cartChangeURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: quantity
        })
      });

      if (!response.ok) throw new Error('Failed to update item quantity');
      
      const cart = await response.json();
      this.updateCartDrawer(cart);
      return true;
    } catch (error) {
      console.error('Error updating item quantity:', error);
      
      // In case of error, make sure all controls are re-enabled
      const item = this.drawer?.querySelector(`[data-cart-item-key="${itemKey}"]`);
      if (item) {
        item.style.opacity = '1';
        const controls = item.querySelectorAll('button, input');
        controls.forEach(control => {
          control.disabled = false;
        });
      }
      
      return false;
    }
  }

  formatMoney(cents) {
    // Basic money formatting
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    });
    return formatter.format(cents / 100);
  }

  rebuildCartItemsHTML(cart, isSecondaryUpdate = false) {
    if (cart.item_count === 0) return;

    // Get the form element that contains the cart items
    const cartForm = this.drawer?.querySelector('#CartDrawerForm');
    if (!cartForm) return;
    
    // Make sure the form is visible if we have items
    cartForm.classList.remove('hidden');
    
    // Get or create the items container
    let itemsContainer = this.drawer?.querySelector('.cart-drawer__items');
    if (!itemsContainer) {
      // If the container doesn't exist, create it
      itemsContainer = document.createElement('div');
      itemsContainer.className = 'cart-drawer__items';
      cartForm.prepend(itemsContainer);
    }
    
    // Ensure container is visible
    itemsContainer.style.display = 'block';
    
    // Find or create the items wrapper
    let itemsWrapper = this.drawer?.querySelector('.cart-drawer__items-wrapper');
    if (!itemsWrapper) {
      itemsWrapper = document.createElement('div');
      itemsWrapper.className = 'cart-drawer__items-wrapper';
      itemsContainer.appendChild(itemsWrapper);
    }
    
    // For secondary updates, compare current and new items to avoid full redraw if possible
    if (isSecondaryUpdate) {
      // Check if we have the same number of items already rendered
      const currentItems = itemsWrapper.querySelectorAll('[data-cart-item]');
      
      // If we have the same number of items, just update quantities and prices
      // instead of completely rebuilding the HTML (which causes flashing)
      if (currentItems.length === cart.items.length) {
        let allItemsMatch = true;
        
        // Check if all current items match cart items by key
        cart.items.forEach((cartItem, index) => {
          const existingItem = currentItems[index];
          const existingItemKey = existingItem?.dataset.cartItemKey;
          
          if (existingItemKey !== cartItem.key) {
            allItemsMatch = false;
          } else {
            // Update just the quantity and price
            const quantityInput = existingItem.querySelector('[data-quantity-input]');
            if (quantityInput) {
              quantityInput.value = cartItem.quantity;
            }
            
            const priceEl = existingItem.querySelector('.cart-drawer__item-final-price');
            if (priceEl) {
              priceEl.textContent = this.formatMoney(cartItem.price);
            }
          }
        });
        
        // If all items matched, we've updated them in place - no need to rebuild HTML
        if (allItemsMatch) {
          return;
        }
      }
    }

    // If we get here, we need to rebuild the HTML from scratch
    itemsWrapper.innerHTML = '';

    // Rebuild HTML for each cart item
    cart.items.forEach(item => {
      const itemHTML = `
        <div class="cart-drawer__item" data-cart-item data-cart-item-key="${item.key}">
          <div class="cart-drawer__item-image">
            <a href="${item.url}">
              <img src="${item.image ? item.image.replace(/(\.[^.]*)$/, '_120x$1') : ''}" alt="${item.title}">
            </a>
          </div>
          <div class="cart-drawer__item-content">
            <div class="cart-drawer__item-details">
              <a href="${item.url}" class="cart-drawer__item-title">${item.product_title}</a>
              ${item.variant_title && item.variant_title !== 'Default Title' ? 
                `<p class="cart-drawer__item-variant">${item.variant_title}</p>` : ''}
              <div class="cart-drawer__item-price-quantity">
                <div class="cart-drawer__item-quantity">
                  <button type="button" class="cart-drawer__quantity-button" data-change-quantity="decrease" aria-label="Decrease quantity">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </button>
                  <input type="number" name="updates[]" value="${item.quantity}" min="0" aria-label="Quantity" data-quantity-input>
                  <button type="button" class="cart-drawer__quantity-button" data-change-quantity="increase" aria-label="Increase quantity">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </button>
                </div>
                <div class="cart-drawer__item-price">
                  ${item.original_line_price !== item.line_price ? 
                    `<s class="cart-drawer__item-original-price">${this.formatMoney(item.original_price)}</s>` : ''}
                  <span class="cart-drawer__item-final-price">${this.formatMoney(item.price)}</span>
                </div>
              </div>
            </div>
            <button type="button" class="cart-drawer__item-remove" data-remove-item aria-label="Remove item">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
          </div>
        </div>
      `;

      itemsWrapper.insertAdjacentHTML('beforeend', itemHTML);
    });
  }
}

export default CartDrawer;